import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    ArrowLeft, User, Camera, Stethoscope, Users, Save, Loader2, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useLanguage();
    const fileInputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [profilePicture, setProfilePicture] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        gpName: '',
        gpAddress: '',
        gpPhone: '',
        nokName: '',
        nokRelationship: '',
        nokPhone: '',
        nokEmail: ''
    });

    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                name: user.name || '',
                gpName: user.gp_details?.name || '',
                gpAddress: user.gp_details?.address || '',
                gpPhone: user.gp_details?.phone || '',
                nokName: user.next_of_kin?.name || '',
                nokRelationship: user.next_of_kin?.relationship || '',
                nokPhone: user.next_of_kin?.phone || '',
                nokEmail: user.next_of_kin?.email || ''
            }));
            if (user.profile_picture) {
                setPreviewUrl(user.profile_picture);
            }
        }
    }, [user]);

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image must be less than 5MB');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePicture(reader.result);
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await axios.put(`${API}/auth/profile`, {
                name: formData.name,
                profile_picture: profilePicture,
                gp_name: formData.gpName,
                gp_address: formData.gpAddress,
                gp_phone: formData.gpPhone,
                next_of_kin_name: formData.nokName,
                next_of_kin_relationship: formData.nokRelationship,
                next_of_kin_phone: formData.nokPhone,
                next_of_kin_email: formData.nokEmail
            });
            toast.success('Profile updated successfully!');
        } catch (err) {
            toast.error('Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="profile-page">
            <div className="max-w-2xl mx-auto px-4">
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t('backToDashboard') || 'Back to Dashboard'}
                </Button>
                
                <h1 className="font-heading text-3xl font-bold mb-2">{t('yourProfile') || 'Your Profile'}</h1>
                <p className="text-muted-foreground mb-8">{t('managePersonalInfo') || 'Manage your personal information and emergency contacts'}</p>

                <form onSubmit={handleSubmit}>
                    <Tabs defaultValue="personal" className="space-y-6">
                        <TabsList className="grid grid-cols-3">
                            <TabsTrigger value="personal"><User className="w-4 h-4 mr-2" />{t('personal') || 'Personal'}</TabsTrigger>
                            <TabsTrigger value="gp"><Stethoscope className="w-4 h-4 mr-2" />{t('gpDetails') || 'GP Details'}</TabsTrigger>
                            <TabsTrigger value="kin"><Users className="w-4 h-4 mr-2" />{t('nextOfKin') || 'Next of Kin'}</TabsTrigger>
                        </TabsList>

                        <TabsContent value="personal">
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('personalInformation') || 'Personal Information'}</CardTitle>
                                    <CardDescription>{t('updateProfilePicture') || 'Update your profile picture and name'}</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <div className="flex flex-col items-center gap-4">
                                        <div 
                                            className="relative w-32 h-32 rounded-full bg-muted flex items-center justify-center overflow-hidden cursor-pointer group"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            {previewUrl ? (
                                                <img src={previewUrl} alt="Profile" className="w-full h-full object-cover" />
                                            ) : (
                                                <User className="w-16 h-16 text-muted-foreground" />
                                            )}
                                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Camera className="w-8 h-8 text-white" />
                                            </div>
                                        </div>
                                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                                        <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                                            {t('changePhoto') || 'Change Photo'}
                                        </Button>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label>{t('fullName') || 'Full Name'}</Label>
                                        <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label>{t('email')}</Label>
                                        <Input value={user?.email || ''} disabled className="bg-muted" />
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="gp">
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('gpDoctorDetails') || 'GP / Doctor Details'}</CardTitle>
                                    <CardDescription>{t('primaryCarePhysician') || 'Your primary care physician information'}</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>{t('doctorName') || 'GP/Doctor Name'}</Label>
                                        <Input value={formData.gpName} onChange={(e) => setFormData({...formData, gpName: e.target.value})} placeholder="Dr. Smith" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>{t('practiceAddress') || 'Practice Address'}</Label>
                                        <Input value={formData.gpAddress} onChange={(e) => setFormData({...formData, gpAddress: e.target.value})} placeholder="123 Medical St, City" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>{t('phoneNumber') || 'Phone Number'}</Label>
                                        <Input value={formData.gpPhone} onChange={(e) => setFormData({...formData, gpPhone: e.target.value})} placeholder="+1 234 567 8900" />
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="kin">
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('nextOfKin') || 'Next of Kin'}</CardTitle>
                                    <CardDescription>{t('emergencyContactInfo') || 'Emergency contact information'}</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>{t('fullName') || 'Full Name'}</Label>
                                        <Input value={formData.nokName} onChange={(e) => setFormData({...formData, nokName: e.target.value})} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>{t('relationship') || 'Relationship'}</Label>
                                        <Input value={formData.nokRelationship} onChange={(e) => setFormData({...formData, nokRelationship: e.target.value})} placeholder="Spouse, Parent, Sibling..." />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>{t('phoneNumber') || 'Phone Number'}</Label>
                                        <Input value={formData.nokPhone} onChange={(e) => setFormData({...formData, nokPhone: e.target.value})} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>{t('email')}</Label>
                                        <Input type="email" value={formData.nokEmail} onChange={(e) => setFormData({...formData, nokEmail: e.target.value})} />
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>

                    <div className="mt-6 flex justify-end">
                        <Button type="submit" className="rounded-full" disabled={loading}>
                            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                            {t('saveChanges') || 'Save Changes'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
